import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import SalonContextSwitcher from '../../components/ui/SalonContextSwitcher';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

// Import components
import ServiceTable from './components/ServiceTable';
import ServiceModal from './components/ServiceModal';
import ServiceFilters from './components/ServiceFilters';
import BulkActionsBar from './components/BulkActionsBar';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import api from 'utils/api';

const ServiceManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedSalon, setSelectedSalon] = useState(
    '651a6b2f8b7a5a1d223e4c90'
  ); // real salonId
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deletingService, setDeletingService] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  //Mock data for services
  const mockServices = [
    {
      id: 1,
      name: 'کوتاهی مو مردانه',
      description: 'کوتاهی مو با قیچی و ماشین اصلاح',
      duration: 45,
      price: 150000,
      assignedEmployees: [1, 2],
    },
    {
      id: 2,
      name: 'رنگ مو زنانه',
      description: 'رنگ کامل مو با رنگ‌های طبیعی',
      duration: 180,
      price: 800000,
      assignedEmployees: [3],
    },
    {
      id: 3,
      name: 'اصلاح صورت',
      description: 'اصلاح کامل صورت با تیغ',
      duration: 30,
      price: 80000,
      assignedEmployees: [1, 4],
    },
    {
      id: 4,
      name: 'فر مو',
      description: 'فر دائمی یا موقت مو',
      duration: 120,
      price: 600000,
      assignedEmployees: [2, 3],
    },
    {
      id: 5,
      name: 'ماساژ سر',
      description: 'ماساژ آرامش‌بخش پوست سر',
      duration: 60,
      price: 200000,
      assignedEmployees: [],
    },
    {
      id: 6,
      name: 'کراتینه مو',
      description: 'صاف کردن و تقویت مو با کراتین',
      duration: 240,
      price: 1200000,
      assignedEmployees: [3],
    },
    {
      id: 7,
      name: 'اپیلاسیون ابرو',
      description: 'اصلاح و طراحی ابرو',
      duration: 20,
      price: 50000,
      assignedEmployees: [2, 4],
    },
    {
      id: 8,
      name: 'هایلایت مو',
      description: 'رنگ قسمتی مو با تکنیک هایلایت',
      duration: 150,
      price: 700000,
      assignedEmployees: [3],
    },
  ];

  //  Mock employees data
  const mockEmployees = [
    { id: 1, name: 'احمد محمدی', role: 'آرایشگر مردانه' },
    { id: 2, name: 'فاطمه احمدی', role: 'آرایشگر زنانه' },
    { id: 3, name: 'مریم کریمی', role: 'متخصص رنگ' },
    { id: 4, name: 'علی رضایی', role: 'آرایشگر' },
  ];

  const getServices = async () => {
    try {
      const response = await api.get(
        `/salons/651a6b2f8b7a5a1d223e4c90/services`
      );
      console.log(response.data);
      const serviceData = Array.isArray(response.data) ? response.data : [];
      setServices(serviceData);
      setFilteredServices(serviceData);
      return serviceData;
    } catch (error) {
      console.error('Error fetching services:', error);
      setServices([]);
      setFilteredServices([]);
      return [];
    }
  };

  useEffect(() => {
    getServices();
    setFilteredServices([]); // Initialize with empty
  }, [selectedSalon]);

  const handleSalonChange = (salonId) => {
    setSelectedSalon(salonId);
    setSelectedServices([]);
  };

  const handleSearch = (searchTerm) => {
    if (!searchTerm?.trim()) {
      setFilteredServices(services);
      return;
    }

    let filtered = services?.filter(
      (service) =>
        service?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        service?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    );
    setFilteredServices(filtered);
  };

  const handleFilter = (filters) => {
    let filtered = [...services];

    // Price range filter
    if (filters?.priceRange?.min) {
      filtered = filtered?.filter(
        (service) => service?.price >= parseFloat(filters?.priceRange?.min)
      );
    }
    if (filters?.priceRange?.max) {
      filtered = filtered?.filter(
        (service) => service?.price <= parseFloat(filters?.priceRange?.max)
      );
    }

    // Duration range filter
    if (filters?.durationRange?.min) {
      filtered = filtered?.filter(
        (service) => service?.duration >= parseInt(filters?.durationRange?.min)
      );
    }
    if (filters?.durationRange?.max) {
      filtered = filtered?.filter(
        (service) => service?.duration <= parseInt(filters?.durationRange?.max)
      );
    }

    // Assignment status filter
    if (filters?.assignmentStatus === 'assigned') {
      filtered = filtered?.filter(
        (service) => service?.assignedEmployees?.length > 0
      );
    } else if (filters?.assignmentStatus === 'unassigned') {
      filtered = filtered?.filter(
        (service) => !service?.assignedEmployees?.length
      );
    }

    setFilteredServices(filtered);
  };

  const handleClearFilters = () => {
    setFilteredServices(services);
  };

  const handleServiceSelect = (serviceId, isSelected) => {
    if (isSelected) {
      setSelectedServices((prev) => [...prev, serviceId]);
    } else {
      setSelectedServices((prev) => prev?.filter((id) => id !== serviceId));
    }
  };

  const handleSelectAll = (serviceIds) => {
    setSelectedServices(serviceIds);
  };

  const handleAddService = () => {
    setEditingService(null);
    setShowServiceModal(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setShowServiceModal(true);
  };

  const handleDeleteService = (service) => {
    setDeletingService(service);
    setSelectedServices([service?.id]);
    setShowDeleteModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedServices?.length === 0) return;
    setDeletingService(null);
    setShowDeleteModal(true);
  };

  const handleSaveService = async (serviceData) => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (editingService) {
        // Update existing service
        const updatedServices = services?.map((service) =>
          service?.id === editingService?.id
            ? { ...serviceData, id: editingService?.id }
            : service
        );
        setServices(updatedServices);
        setFilteredServices(updatedServices);
      } else {
        // Add new service
        const newService = { ...serviceData, id: Date.now() };
        const updatedServices = [...services, newService];
        setServices(updatedServices);
        setFilteredServices(updatedServices);
      }
    } catch (error) {
      console.error('Error saving service:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const updatedServices = services?.filter(
        (service) => !selectedServices?.includes(service?.id)
      );
      setServices(updatedServices);
      setFilteredServices(updatedServices);
      setSelectedServices([]);
      setShowDeleteModal(false);
      setDeletingService(null);
    } catch (error) {
      console.error('Error deleting services:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkAssign = (employeeIds) => {
    // Simulate bulk assignment
    console.log(
      'Bulk assign services:',
      selectedServices,
      'to employees:',
      employeeIds
    );
    setSelectedServices([]);
  };

  return (
    <>
      <Helmet>
        <title>مدیریت خدمات - پنل مدیریت سالن</title>
        <meta
          name="description"
          content="مدیریت کامل خدمات سالن شامل قیمت‌گذاری، مدت زمان و تخصیص کارکنان"
        />
      </Helmet>
      <div className="min-h-screen bg-background" dir="rtl">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        {/* <Header
          isCollapsed={false}
          onToggleSidebar={() => setSidebarOpen(true)}
        /> */}

        <main className="lg:mr-60 pt-16">
          <div className="p-6 space-y-6">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <BreadcrumbNavigation />
                <div className="mt-2">
                  <h1 className="text-2xl font-bold text-foreground">
                    مدیریت خدمات
                  </h1>
                  <p className="text-muted-foreground mt-1">
                    مدیریت کامل خدمات سالن شامل قیمت‌گذاری و تخصیص کارکنان
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  onClick={handleAddService}
                  iconName="Plus"
                  iconPosition="left"
                  className="whitespace-nowrap">
                  افزودن خدمت
                </Button>
              </div>
            </div>

            {/* Salon Context Switcher */}
            <SalonContextSwitcher
              selectedSalon={selectedSalon}
              onSalonChange={handleSalonChange}
            />

            {/* Service Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Scissors" size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">کل خدمات</p>
                    <p className="text-xl font-bold text-foreground">
                      {services?.length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="Users" size={20} className="text-success" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">تخصیص یافته</p>
                    <p className="text-xl font-bold text-foreground">
                      {
                        services?.filter(
                          (s) => s?.assignedEmployees?.length > 0
                        )?.length
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Icon
                      name="AlertCircle"
                      size={20}
                      className="text-warning"
                    />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">بدون تخصیص</p>
                    <p className="text-xl font-bold text-foreground">
                      {
                        services?.filter((s) => !s?.assignedEmployees?.length)
                          ?.length
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Clock" size={20} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      میانگین زمان
                    </p>
                    <p className="text-xl font-bold text-foreground">
                      {Math.round(
                        services?.reduce((acc, s) => acc + s?.duration, 0) /
                          services?.length || 0
                      )}{' '}
                      دقیقه
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            {/* <ServiceFilters
              onSearch={handleSearch}
              onFilter={handleFilter}
              onClearFilters={handleClearFilters}
              totalServices={services?.length}
              filteredServices={filteredServices?.length}
            /> */}

            {/* Services Table */}
            <ServiceTable
              services={services}
              selectedServices={selectedServices}
              onServiceSelect={handleServiceSelect}
              onSelectAll={handleSelectAll}
              onEdit={handleEditService}
              onDelete={handleDeleteService}
              onBulkAction={handleBulkDelete}
            />

            {/* Bulk Actions Bar */}
            <BulkActionsBar
              selectedCount={selectedServices?.length}
              onBulkDelete={handleBulkDelete}
              onBulkAssign={handleBulkAssign}
              onClearSelection={() => setSelectedServices([])}
              employees={mockEmployees}
            />

            {/* Service Modal */}
            <ServiceModal
              isOpen={showServiceModal}
              onClose={() => setShowServiceModal(false)}
              onSave={handleSaveService}
              service={editingService}
              employees={mockEmployees}
              selectedSalon={selectedSalon}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
              isOpen={showDeleteModal}
              onClose={() => setShowDeleteModal(false)}
              onConfirm={handleConfirmDelete}
              service={deletingService}
              selectedServices={selectedServices}
              isLoading={isLoading}
            />
          </div>
        </main>
      </div>
    </>
  );
};

export default ServiceManagement;
